import React, { useMemo, useState } from 'react';
import { View, Text } from 'react-native';
import { Clock, CheckCircle, XCircle, User, Building2 } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';

export interface AccessRequest {
  id: string;
  investorId: string;
  investorName: string;
  investorFirm?: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  respondedAt?: string;
}

type Filter = 'pending' | 'approved' | 'rejected';

interface AccessRequestsSectionProps {
  requests: AccessRequest[];
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onRevoke?: (id: string) => void;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Granted' },
  { key: 'rejected', label: 'Revoked' },
];

export function AccessRequestsSection({
  requests,
  onApprove,
  onReject,
  onRevoke,
}: AccessRequestsSectionProps) {
  const colors = useThemeColors();
  const [filter, setFilter] = useState<Filter>('pending');

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    for (const r of requests) c[r.status] += 1;
    return c;
  }, [requests]);

  const filtered = useMemo(
    () => requests.filter((r) => r.status === filter),
    [requests, filter],
  );

  const statusBadge = (status: AccessRequest['status']) => {
    if (status === 'pending') {
      return { label: 'Pending', color: colors.warning, bg: colors.amber.light, Icon: Clock };
    }
    if (status === 'approved') {
      return { label: 'Granted', color: colors.success, bg: colors.traffic.greenLight, Icon: CheckCircle };
    }
    return { label: 'Revoked', color: colors.error, bg: colors.red.light, Icon: XCircle };
  };

  return (
    <View
      style={{
        backgroundColor: colors.surface.primary,
        borderRadius: 16,
        padding: 16,
        shadowColor: '#000',
        shadowOpacity: 0.05,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
        elevation: 2,
      }}
    >
      {/* Filter chips */}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const count = counts[f.key];
          return (
            <AnimatedPressable
              key={f.key}
              onPress={() => setFilter(f.key)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 999,
                backgroundColor: active ? colors.primary[600] : colors.surface.secondary,
                borderWidth: 1,
                borderColor: active ? colors.primary[600] : colors.border.light,
              }}
            >
              <Text
                style={{
                  color: active ? '#fff' : colors.text.secondary,
                  fontSize: 12,
                  fontWeight: '700',
                }}
                numberOfLines={1}
              >
                {f.label}
              </Text>
              {count > 0 && (
                <View
                  style={{
                    minWidth: 18,
                    paddingHorizontal: 5,
                    height: 18,
                    borderRadius: 9,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: active
                      ? 'rgba(255,255,255,0.25)'
                      : colors.primary[100],
                  }}
                >
                  <Text
                    style={{
                      color: active ? '#fff' : colors.primary[700],
                      fontSize: 10,
                      fontWeight: '700',
                    }}
                  >
                    {count}
                  </Text>
                </View>
              )}
            </AnimatedPressable>
          );
        })}
      </View>

      {/* List */}
      {filtered.length === 0 ? (
        <View style={{ paddingVertical: 24, alignItems: 'center' }}>
          <Text
            style={{ color: colors.text.tertiary, fontSize: 13 }}
            numberOfLines={2}
          >
            {filter === 'pending'
              ? 'No pending access requests.'
              : filter === 'approved'
                ? 'No investors currently have access.'
                : 'No revoked access yet.'}
          </Text>
        </View>
      ) : (
        <View style={{ gap: 10 }}>
          {filtered.map((req) => {
            const badge = statusBadge(req.status);
            const BadgeIcon = badge.Icon;
            return (
              <View
                key={req.id}
                style={{
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  borderRadius: 12,
                  padding: 12,
                  backgroundColor: colors.surface.secondary,
                }}
              >
                {/* Top row: investor + status */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 8,
                  }}
                >
                  <View
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      backgroundColor: colors.primary[50],
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <User size={16} color={colors.primary[600]} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={{
                        color: colors.text.primary,
                        fontSize: 13,
                        fontWeight: '700',
                      }}
                      numberOfLines={1}
                    >
                      {req.investorName}
                    </Text>
                    {req.investorFirm && (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                          marginTop: 2,
                        }}
                      >
                        <Building2 size={11} color={colors.text.tertiary} />
                        <Text
                          style={{ color: colors.text.tertiary, fontSize: 11 }}
                          numberOfLines={1}
                        >
                          {req.investorFirm}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      paddingHorizontal: 8,
                      paddingVertical: 3,
                      borderRadius: 999,
                      backgroundColor: badge.bg,
                      flexShrink: 0,
                    }}
                  >
                    <BadgeIcon size={11} color={badge.color} />
                    <Text
                      style={{
                        color: badge.color,
                        fontSize: 10,
                        fontWeight: '700',
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                      }}
                      numberOfLines={1}
                    >
                      {badge.label}
                    </Text>
                  </View>
                </View>

                {/* Optional message */}
                {req.message && (
                  <Text
                    style={{
                      color: colors.text.secondary,
                      fontSize: 12,
                      lineHeight: 16,
                      marginBottom: 8,
                    }}
                    numberOfLines={3}
                  >
                    “{req.message}”
                  </Text>
                )}

                {/* Timestamps */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                  <Text
                    style={{ color: colors.text.tertiary, fontSize: 11 }}
                    numberOfLines={1}
                  >
                    Requested {formatDate(req.createdAt)}
                  </Text>
                  {req.respondedAt && (
                    <Text
                      style={{ color: colors.text.tertiary, fontSize: 11 }}
                      numberOfLines={1}
                    >
                      ·  {req.status === 'approved' ? 'Granted' : 'Revoked'} {formatDate(req.respondedAt)}
                    </Text>
                  )}
                </View>

                {/* Actions */}
                {req.status === 'pending' && (
                  <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
                    <AnimatedPressable
                      onPress={() => onApprove?.(req.id)}
                      style={{
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        height: 36,
                        paddingHorizontal: 12,
                        borderRadius: 10,
                        backgroundColor: colors.success,
                      }}
                    >
                      <CheckCircle size={14} color="#fff" />
                      <Text
                        style={{ color: '#fff', fontSize: 12, fontWeight: '700' }}
                        numberOfLines={1}
                      >
                        Grant Access
                      </Text>
                    </AnimatedPressable>
                    <AnimatedPressable
                      onPress={() => onReject?.(req.id)}
                      style={{
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        height: 36,
                        paddingHorizontal: 12,
                        borderRadius: 10,
                        backgroundColor: colors.surface.primary,
                        borderWidth: 1,
                        borderColor: colors.border.medium,
                      }}
                    >
                      <XCircle size={14} color={colors.text.secondary} />
                      <Text
                        style={{ color: colors.text.secondary, fontSize: 12, fontWeight: '700' }}
                        numberOfLines={1}
                      >
                        Decline
                      </Text>
                    </AnimatedPressable>
                  </View>
                )}
                {req.status === 'approved' && onRevoke && (
                  <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 4 }}>
                    <AnimatedPressable
                      onPress={() => onRevoke(req.id)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        height: 32,
                        paddingHorizontal: 12,
                        borderRadius: 10,
                        backgroundColor: colors.surface.primary,
                        borderWidth: 1,
                        borderColor: colors.border.medium,
                      }}
                    >
                      <XCircle size={13} color={colors.error} />
                      <Text
                        style={{ color: colors.error, fontSize: 12, fontWeight: '700' }}
                        numberOfLines={1}
                      >
                        Revoke
                      </Text>
                    </AnimatedPressable>
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}
